/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { LabelDisplayedRowsArgs, Table, TableBody, TablePagination, TableRow } from '@mui/material';
import withStyles from '@mui/styles/withStyles';

interface Props {
	totalRows: number;
	rowLabelPlural: string;
	page: number;
	rowsPerPage: number;
	rowsPerPageOptions: readonly number[];
	handleChangePage: (newPage: number) => void;
	handleChangeRowsPerPage: (newRowsPerPage: number) => void;
}

const StyledTablePagination = withStyles({
	spacer: {
		flex: '0'
	}
})(TablePagination);

class TablePaginationView extends React.Component<Props> {
	constructor(props: Props) {
		super(props);

		this.handleChangePage = this.handleChangePage.bind(this);
		this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
	}

	handleChangePage(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void {
		if (event) {
			event.preventDefault();
		}

		this.props.handleChangePage(newPage);
	}

	handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): void {
		event.preventDefault();
		const newRowsPerPage = +event.target.value;

		this.props.handleChangeRowsPerPage(newRowsPerPage);
	}

	render(): JSX.Element {
		const { totalRows, rowLabelPlural, page, rowsPerPage, rowsPerPageOptions } = this.props;

		return (
			<Table>
				<TableBody>
					<TableRow>
						<StyledTablePagination
							rowsPerPageOptions={[...rowsPerPageOptions]} // make the array mutable for consumer
							count={totalRows}
							rowsPerPage={rowsPerPage}
							page={page}
							backIconButtonProps={{
								'aria-label': 'previous page'
							}}
							nextIconButtonProps={{
								'aria-label': 'next page'
							}}
							onPageChange={this.handleChangePage}
							onRowsPerPageChange={this.handleChangeRowsPerPage}
							labelRowsPerPage={`Anzahl ${rowLabelPlural} pro Seite`}
							labelDisplayedRows={(info: LabelDisplayedRowsArgs): string => `${info.from}-${info.to} von ${info.count}`}
						/>
					</TableRow>
				</TableBody>
			</Table>
		);
	}
}

export default TablePaginationView;
